// Single source of truth for all level data
export interface CompletionMission {
  type: 'completion';
  id: string;
  text: string;
  gameId?: string; // Optional associated game for launchable completion missions
  completed: boolean;
}

export interface MeasurableMission {
  type: 'measurable';
  id: string;
  text: string;
  target: number;
  current: number;
  unit: string; // e.g., "achievements", "points", "games"
  gameId?: string; // Associated game for achievement-based missions
}

export type Mission = CompletionMission | MeasurableMission;

export interface Level {
  id: string;
  title: string;
  missions: Mission[];
  sideQuests?: Mission[];
}

// Temporary promo mission flag (set to false to remove)
const SHOW_WINTER_2026_PROMO: boolean = true;

// ID used across mapping to activities
const WINTER_2026_PROMO_MISSION_ID = 'winter-2026-promo';

const getWinter2026PromoMission = (): CompletionMission => ({
  type: 'completion',
  id: WINTER_2026_PROMO_MISSION_ID,
  text: 'Winter Kickoff and Parent Accounts!',
  completed: false
});

export const levels: Level[] = [
  // Rhythm Classes (2)
  {
    id: '1',
    title: 'Level 1: Rhythm Basics',
    missions: [
      {
        type: 'measurable',
        id: 'artists-genres-1',
        text: 'My Favorite Musicians',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'my-favorite-musicians'
      },
      {
        type: 'completion',
        id: 'rhythm-101-1',
        text: 'Rhythm 101',
        completed: false
      },
      {
        type: 'completion',
        id: 'beat-making-intro-2',
        text: 'Intro to Beat Making: Overture Music Lab',
        gameId: 'overture-music-lab',
        completed: false
      },
      {
        type: 'measurable',
        id: 'rhythm-rally-achievements-1',
        text: 'Rhythm Rally',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'rhythm-rally'
      },
      {
        type: 'completion',
        id: 'rhythm-puzzle-achievements-1',
        text: 'Rhythm Puzzle',
        gameId: 'rhythm-puzzle',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-1',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ]
  },
  {
    id: '2',
    title: 'Level 2: Advanced Rhythm',
    missions: [
      {
        type: 'completion',
        id: 'rhythm-warmup-2',
        text: 'Rhythm Simon Says',
        completed: false
      },
      {
        type: 'measurable',
        id: 'beat-decoders-2',
        text: 'Beat Decoders',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'beat-decoders'
      },
      {
        type: 'completion',
        id: 'showcase-2',
        text: 'Showcase: Student Creations',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-2',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'measurable',
        id: 'rhythm-rally-side-quest-2',
        text: 'Rhythm Rally',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'rhythm-rally'
      },
      {
        type: 'completion',
        id: 'rhythm-puzzle-side-quest-2',
        text: 'Rhythm Puzzle',
        gameId: 'rhythm-puzzle',
        completed: false
      },
      {
        type: 'completion',
        id: 'rocket-race-side-quest-2',
        text: 'Rocket Race: Straight or Swung',
        gameId: 'rocket-race',
        completed: false
      }
    ]
  },
  // Harmony Classes (2)
  {
    id: '3',
    title: 'Level 3: Harmony Basics',
    missions: [
      {
        type: 'completion',
        id: 'rocket-race-harmony-3',
        text: 'Rocket Race: Harmony',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'completion',
        id: 'yeet-the-scoop-3',
        text: 'Yeet the Scoop',
        completed: false
      },
      {
        type: 'completion',
        id: 'scale-the-tower-3',
        text: 'Scale the Tower',
        completed: false
      },
      {
        type: 'measurable',
        id: 'chordstruction-3',
        text: 'Chordstruction',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'chordstruction'
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-3',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: []
  },
  {
    id: '4',
    title: 'Level 4: Advanced Harmony',
    missions: [
      {
        type: 'completion',
        id: 'rocket-race-harmony-4',
        text: 'Rocket Race: Major/Minor',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'completion',
        id: 'harmonidome-4',
        text: 'Harmonidome',
        gameId: 'harmonidome-teacher',
        completed: false
      },
      {
        type: 'completion',
        id: 'showcase-4',
        text: 'Showcase: Student Creations',
        completed: false
      },
      {
        type: 'measurable',
        id: 'piano-clicker-4',
        text: 'Piano Clicker',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'piano-clicker'
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-4',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'completion',
        id: 'yeet-the-scoop-side-quest-4',
        text: 'Yeet the Scoop',
        completed: false
      }
    ]
  },
  // Instrument Classes (2)
  {
    id: '5',
    title: 'Level 5: Instrument Families',
    missions: [
      {
        type: 'completion',
        id: 'instrument-families-5',
        text: 'Introduce Instrument Families',
        completed: false
      },
      {
        type: 'completion',
        id: 'instruments-pop-music-5',
        text: 'Identify Instruments in Videos',
        completed: false
      },
      {
        type: 'completion',
        id: 'rocket-race-instruments-easy-5',
        text: 'Rocket Race: Instruments (Easy)',
        gameId: 'rocket-race',
        completed: false
      },
      
      {
        type: 'completion',
        id: 'incredibox-5',
        text: 'Play Incredibox',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-5',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ]
  },
  {
    id: '6',
    title: 'Level 6: Instrumentation',
    missions: [
      {
        type: 'completion',
        id: 'rocket-race-instruments-easy-6',
        text: 'Rocket Race: Instruments (Easy)',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'completion',
        id: 'harmonidome-instruments-6',
        text: 'Harmonidome (Instruments)',
        gameId: 'harmonidome-teacher',
        completed: false
      },
      {
        type: 'completion',
        id: 'showcase-6',
        text: 'Showcase: Student Creations',
        completed: false
      },
      {
        type: 'completion',
        id: 'rocket-race-instruments-hard-6',
        text: 'Rocket Race: Instruments (Hard)',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-6',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'completion',
        id: 'incredibox-side-quest-6',
        text: 'Incredibox',
        completed: false
      },
      {
        type: 'completion',
        id: 'beat-decoders-instruments-6',
        text: 'Beat Decoders (Instruments)',
        completed: false
      }
    ]
  },
  // Melody Classes (2)
  {
    id: '7',
    title: 'Level 7: Melody Basics',
    missions: [
      {
        type: 'measurable',
        id: 'tune-testers-7',
        text: 'Tune Testers',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'tune-testers'
      },
      {
        type: 'measurable',
        id: 'step-skip-repeat-7',
        text: 'The "Step, Skip, or Repeat" Game',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'step-skip-repeat'
      },
      {
        type: 'measurable',
        id: 'melody-shaper-7',
        text: 'Melody Shaper',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'melody-shaper'
      },
      {
        type: 'measurable',
        id: 'melopedes-hot-cross-buns-7',
        text: 'Melopedes: Hot Cross Buns',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'melopedes'
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-7',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'measurable',
        id: 'piano-clicker-side-quest-7',
        text: 'Piano Clicker',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'piano-clicker'
      }
    ]
  },
  {
    id: '8',
    title: 'Level 8: Melody Composition',
    missions: [
      {
        type: 'completion',
        id: 'rocket-race-melody-8',
        text: 'Rocket Race (Meme Tunes & Composers)',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'measurable',
        id: 'tuna-8',
        text: 'Tuna',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'tuna'
      },
      {
        type: 'measurable',
        id: 'melopedes-composer-8',
        text: 'Melopedes Composer',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'melopedes'
      },
      {
        type: 'completion',
        id: 'showcase-8',
        text: 'Showcase: Student Creations',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-8',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'measurable',
        id: 'tune-testers-8-side-quest',
        text: 'Tune Testers',
        target: 3,
        current: 0,
        unit: 'achievements',
        gameId: 'tune-testers'
      },
      {
        type: 'measurable',
        id: 'step-skip-repeat-8-side-quest',
        text: 'Step, Skip, or Repeat',
        target: 3,
        current: 0,
        unit: 'achievements',
        gameId: 'step-skip-repeat'
      },
      {
        type: 'measurable',
        id: 'melody-shaper-8-side-quest',
        text: 'Melody Shaper',
        target: 3,
        current: 0,
        unit: 'achievements',
        gameId: 'melody-shaper'
      },
      {
        type: 'measurable',
        id: 'piano-clicker-side-quest-8',
        text: 'Piano Clicker',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'piano-clicker'
      },
      {
        type: 'completion',
        id: 'key-smashers-side-quest-8',
        text: 'Key Smashers',
        completed: false
      }
    ]
  },
  // Form Classes (2)
  {
    id: '9',
    title: 'Level 9: Musical Form',
    missions: [
      {
        type: 'completion',
        id: 'what-is-musical-form-9',
        text: 'Warm-Up: What Is Musical Form?',
        completed: false
      },
      {
        type: 'completion',
        id: 'react-attack-9',
        text: 'React Attack',
        gameId: 'react-attack-teacher',
        completed: false
      },
      {
        type: 'completion',
        id: 'chrome-music-lab-9',
        text: 'Overture Music Lab: ABA Form',
        gameId: 'overture-music-lab',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-9',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'measurable',
        id: 'beat-decoders-side-quest-9',
        text: 'Beat Decoders',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'beat-decoders'
      },
      {
        type: 'measurable',
        id: 'melopedes-side-quest-9',
        text: 'Melopedes',
        target: 2,
        current: 0,
        unit: 'achievements',
        gameId: 'melopedes'
      }
    ]
  },
  {
    id: '10',
    title: 'Level 10: Dynamics',
    missions: [
      {
        type: 'completion',
        id: 'rocket-race-dynamics-10',
        text: 'Rocket Race: Dynamics',
        gameId: 'rocket-race',
        completed: false
      },
      {
        type: 'measurable',
        id: 'flappy-muse-dynamics-10',
        text: 'Flappy Muse',
        target: 1,
        current: 0,
        unit: 'achievements',
        gameId: 'flappy-muse'
      },
      {
        type: 'completion',
        id: 'inside-orchestra-dynamics-10',
        text: 'Inside the Orchestra: Dynamic Game',
        completed: false
      },
      {
        type: 'completion',
        id: 'dynamics-rainstorm-10',
        text: 'Classroom Activity: Dynamics Rainstorm',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-10',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      {
        type: 'completion',
        id: 'spectrogram-side-quest-10',
        text: 'Spectrogram',
        completed: false
      },
      {
        type: 'completion',
        id: 'soundgym-eq-side-quest-10',
        text: 'SoundGym EQ Game',
        completed: false
      }
    ]
  },
  // Dynamics Class (1)
  {
    id: '11',
    title: 'Level 11: Genres',
    missions: [
      { type: 'completion', id: 'genres-overview-11', text: 'What is that musical genre?', completed: false },
      { type: 'completion', id: 'listening-exercise-11', text: 'Listening/imagination & move to the music', completed: false },
      { type: 'completion', id: 'artists-genres-11', text: 'Move to the Music!', completed: false },
      { type: 'measurable', id: 'my-favorite-musicians-11', text: 'My Favorite Musicians', target: 2, current: 0, unit: 'achievements', gameId: 'my-favorite-musicians' },
      { type: 'measurable', id: 'real-or-ai-11', text: 'Real or AI (Brain Break)', target: 1, current: 0, unit: 'achievements', gameId: 'real-or-ai' },
      { type: 'completion', id: 'suno-ai-11', text: 'Suno AI genre - song generation', completed: false },
      { type: 'measurable', id: 'reflect-and-progress-11', text: 'Reflection', target: 1, current: 0, unit: 'students' }
    ],
    sideQuests: [
      { type: 'completion', id: 'genre-quiz-score-11', text: 'Rocket Race - genres', completed: false },
    ]
  },
  // Final Showcase
  {
    id: '12',
    title: 'Level 12: Greatest Hits',
    missions: [
      {
        type: 'completion',
        id: 'favorite-missions-12',
        text: 'Revisit your Favorite Hits',
        completed: false
      },
      {
        type: 'completion',
        id: 'showcase-12',
        text: 'Showcase',
        completed: false
      },
      {
        type: 'completion',
        id: 'rewards-12',
        text: 'Level up and Rewards',
        completed: false
      },
      {
        type: 'measurable',
        id: 'reflect-and-progress-12',
        text: 'Reflection',
        target: 1,
        current: 0,
        unit: 'students'
      }
    ],
    sideQuests: [
      { type: 'measurable', id: 'artists-genres-1-side-quest-12', text: 'My Favorite Musicians', target: 3, current: 0, unit: 'achievements', gameId: 'my-favorite-musicians' },
      { type: 'measurable', id: 'rhythm-rally-side-quest-2-side-quest-12', text: 'Rhythm Rally', target: 4, current: 0, unit: 'achievements', gameId: 'rhythm-rally' },
      { type: 'measurable', id: 'beat-decoders-side-quest-9-side-quest-12', text: 'Beat Decoders', target: 4, current: 0, unit: 'achievements', gameId: 'beat-decoders' },
      { type: 'measurable', id: 'piano-clicker-side-quest-8-side-quest-12', text: 'Piano Clicker', target: 3, current: 0, unit: 'achievements', gameId: 'piano-clicker' },
      { type: 'measurable', id: 'tune-testers-8-side-quest-side-quest-12', text: 'Tune Testers', target: 5, current: 0, unit: 'achievements', gameId: 'tune-testers' },
      { type: 'measurable', id: 'step-skip-repeat-8-side-quest-side-quest-12', text: 'Step, Skip, or Repeat', target: 5, current: 0, unit: 'achievements', gameId: 'step-skip-repeat' },
      { type: 'measurable', id: 'melody-shaper-8-side-quest-side-quest-12', text: 'Melody Shaper', target: 5, current: 0, unit: 'achievements', gameId: 'melody-shaper' },
      { type: 'measurable', id: 'melopedes-side-quest-9-side-quest-12', text: 'Melopedes', target: 4, current: 0, unit: 'achievements', gameId: 'melopedes' },
      { type: 'measurable', id: 'react-attack-9-side-quest-12', text: 'React Attack', target: 3, current: 0, unit: 'achievements', gameId: 'react-attack-student' },
      { type: 'measurable', id: 'flappy-muse-dynamics-10-side-quest-12', text: 'Flappy Muse', target: 3, current: 0, unit: 'achievements', gameId: 'flappy-muse' },
      // Genre Quiz is implemented as a Rocket Race (genres) mission in this curriculum.
      { type: 'measurable', id: 'genre-quiz-score-12-side-quest-12', text: 'Genre Quiz', target: 5, current: 0, unit: 'achievements', gameId: 'rocket-race' }
    ]
  }
];

// Utility functions
export const getLevelById = (id: string): Level | undefined => {
  const base = levels.find(level => level.id === id);
  if (!base) return undefined;
  if (!SHOW_WINTER_2026_PROMO) return base;
  // Prepend the promo mission in-place once to preserve object identity across renders
  const alreadyIncluded = base.missions.some(m => m.id === WINTER_2026_PROMO_MISSION_ID);
  if (!alreadyIncluded) {
    base.missions = [getWinter2026PromoMission(), ...base.missions];
  }
  return base;
};

export const getLevelIndex = (id: string) => levels.findIndex(level => level.id === id);

export const getNextLevel = (currentId: string) => {
  const currentIndex = getLevelIndex(currentId);
  const nextIndex = currentIndex + 1;
  return nextIndex < levels.length ? levels[nextIndex] : levels[levels.length - 1];
};

export const getLevelStatus = (
  levelId: string, 
  currentLevelId: string, 
  completedLevels: string[] = []
): 'completed' | 'next' | 'upcoming' => {
  // Check if level is explicitly marked as completed
  if (completedLevels.includes(levelId)) return 'completed';
  
  // Find the highest completed level
  const completedLevelNumbers = completedLevels
    .map(id => parseInt(id))
    .filter(num => !isNaN(num));
  
  if (completedLevelNumbers.length > 0) {
    const highestCompleted = Math.max(...completedLevelNumbers);
    const currentLevelNumber = parseInt(levelId);
    
    // Level immediately after highest completed is "next"
    if (currentLevelNumber === highestCompleted + 1) {
      return 'next';
    }
  } else {
    // If no levels completed, level 1 is next
    if (levelId === '1') {
      return 'next';
    }
  }
  
  // Everything else is upcoming
  return 'upcoming';
};

export const getShortLevelName = (title: string): string => {
  if (title.includes('Greatest Hits')) return 'Showcase';
  if (title.includes('Genres')) return 'Genres';
  if (title.includes('Rhythm')) return 'Rhythm';
  if (title.includes('Harmony')) return 'Harmony';
  if (title.includes('Instrument')) return 'Instrument';
  if (title.includes('Melody')) return 'Melody';
  if (title.includes('Form')) return 'Form';
  if (title.includes('Dynamics')) return 'Dynamics';
  if (title.includes('Expression')) return 'Expression';
  return title.split(' ')[0];
}; 